import React, {useEffect, useRef} from 'react';
import {Box, Button, CircularProgress} from '@mui/material';
import {Typography} from '@mui/joy';
import {useTranslation} from 'react-i18next';
import {
    CancelFilledRounded,
    CheckCircleFilledRounded,
    RadioButtonUncheckedRounded,
    ReplayRounded,
} from '@mui-symbols-material/w300';
import {DuplicateItem} from '../types';

interface Props {
    items: DuplicateItem[];
    running: boolean;
    cancelled: boolean;
    percent: number;
    successCount: number;
    errorCount: number;
    etaMs: number | null;
    onRetry: (item: DuplicateItem) => void;
    onCancel: () => void;
    onClose?: () => void;
}

const formatEta = (ms: number): string => {
    const totalSeconds = Math.max(1, Math.ceil(ms / 1000));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }
    return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
};

const StatusIcon = ({item}: { item: DuplicateItem }) => {
    switch (item.status) {
        case 'done':
            return <CheckCircleFilledRounded sx={{color: 'success.main', fontSize: 22}}/>;
        case 'error':
            return <CancelFilledRounded sx={{color: 'error.main', fontSize: 22}}/>;
        case 'inProgress':
            return <CircularProgress size={18}/>;
        default:
            return <RadioButtonUncheckedRounded sx={{color: 'text.disabled', fontSize: 22}}/>;
    }
};

const CreateStep = ({
    items,
    running,
    cancelled,
    percent,
    successCount,
    errorCount,
    etaMs,
    onRetry,
    onCancel,
    onClose,
}: Props) => {
    const {t} = useTranslation();

    const successPercent = items.length > 0 ? (successCount / items.length) * 100 : 0;
    const errorPercent = items.length > 0 ? (errorCount / items.length) * 100 : 0;

    // Auto-follow the queue: keep the in-progress row visible while creating.
    // As soon as an error occurs, stop following so the user can read and
    // retry the failed entries without the list scrolling away.
    const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const inProgressId = items.find((item) => item.status === 'inProgress')?.id;
    const hasError = errorCount > 0;

    useEffect(() => {
        if (inProgressId === undefined || hasError) return;
        rowRefs.current[inProgressId]?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }, [inProgressId, hasError]);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            {/* Stacked progress bar: success and error segments side by side */}
            <Box>
                <Box sx={{
                    position: 'relative',
                    height: 8,
                    borderRadius: '4px',
                    backgroundColor: '#d9d9d9',
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${successPercent}%`,
                        backgroundColor: 'primary.main',
                        transition: 'width 0.3s ease',
                    }}/>
                    <Box sx={{
                        position: 'absolute',
                        left: `${successPercent}%`,
                        top: 0,
                        bottom: 0,
                        width: `${errorPercent}%`,
                        backgroundColor: 'error.main',
                        transition: 'width 0.3s ease, left 0.3s ease',
                    }}/>
                </Box>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1}}>
                    <Box sx={{display: 'flex', gap: 0.5}}>
                        <Typography level="body-sm" sx={{color: 'text.secondary'}}>
                            {t('{{success}} successful', {success: successCount})}
                        </Typography>
                        {errorCount > 0 && (
                            <Typography level="body-sm" sx={{color: 'error.main', fontWeight: 'bold'}}>
                                {' · '}{t('{{errors}} errors', {errors: errorCount})}
                            </Typography>
                        )}
                    </Box>
                    <Typography level="body-sm" sx={{color: 'text.secondary'}}>
                        {Math.round(percent)}{'%'}
                        {etaMs !== null && ` · ${t('~{{time}} remaining', {time: formatEta(etaMs)})}`}
                    </Typography>
                </Box>
            </Box>

            {cancelled && (
                <Typography level="body-sm" sx={{color: 'text.secondary'}}>
                    {t('Creation cancelled. Nodes already created remain.')}
                </Typography>
            )}

            {/* Scrollable item list. The negative margin compensates the rows'
                inner padding so their content lines up with the content edges
                while the error background still bleeds slightly past them. */}
            <Box sx={{maxHeight: '40vh', overflowY: 'auto', mx: -1.5}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                    {items.map((item) => (
                        <Box
                            key={item.id}
                            ref={(el: HTMLDivElement | null) => {
                                rowRefs.current[item.id] = el;
                            }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                px: 1.5,
                                py: 1,
                                borderRadius: '8px',
                                ...(item.status === 'error' && {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                }),
                            }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Box sx={{display: 'flex', alignItems: 'center', width: 24, flexShrink: 0, justifyContent: 'center'}}>
                                    <StatusIcon item={item}/>
                                </Box>
                                <Typography
                                    level="body-md"
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        ...(item.status === 'error' && {color: 'error.main', fontWeight: 'bold'}),
                                        ...(item.status === 'pending' && {color: 'text.secondary'}),
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                {item.status === 'inProgress' && (
                                    <Typography level="body-sm" sx={{color: 'text.secondary'}}>
                                        {t('in progress …')}
                                    </Typography>
                                )}
                                {item.status === 'error' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<ReplayRounded/>}
                                        onClick={() => onRetry(item)}
                                        disabled={running}
                                    >
                                        {t('Retry')}
                                    </Button>
                                )}
                            </Box>
                            {item.status === 'error' && item.errorMessage && (
                                <Typography level="body-sm" sx={{color: 'error.main', pl: '36px'}}>
                                    {item.errorMessage}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>

            {running ? (
                <Button fullWidth variant="contained" onClick={onCancel}>
                    {t('Cancel')}
                </Button>
            ) : (
                <Button fullWidth variant="contained" onClick={onClose}>
                    {t('Close')}
                </Button>
            )}
        </Box>
    );
};

export default CreateStep;
